import React from "react";
import {
  Box,
  Typography,
  Paper,
  Breadcrumbs,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HomeIcon from "@mui/icons-material/Home";
import RefreshIcon from "@mui/icons-material/Refresh";
import { KnowledgehubFolderResponse, KnowledgehubFileResponse } from "../../../../app/models/responseModels/knowledgehubResponse";

export type BreadcrumbItem = { id: number | null; name: string; onClick: () => void };

type Props = {
  folders: KnowledgehubFolderResponse[];
  files: KnowledgehubFileResponse[];
  breadcrumbPath: BreadcrumbItem[];
  showBack: boolean;
  onBack: () => void;
  onRefresh: () => void;
};

export default function KnowledgehubHeader({ folders, files, breadcrumbPath, showBack, onBack, onRefresh }: Props) {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        pb: 2,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        bgcolor: "white",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          color: "transparent",
          mb: 1,
        }}
      >
        Knowledge Hub
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Centralized repository.
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {showBack && (
            <Tooltip title="Go Back">
              <IconButton
                onClick={onBack}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ ml: showBack ? 1 : 0 }}
          >
            {breadcrumbPath.map((item, index) => {
              const isLast = index === breadcrumbPath.length - 1;
              return (
                <Typography
                  key={item.id ?? "home"}
                  variant="body2"
                  onClick={!isLast ? item.onClick : undefined}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: !isLast ? "pointer" : "default",
                    color: !isLast ? theme.palette.primary.main : theme.palette.text.primary,
                    fontWeight: !isLast ? 500 : 600,
                    "&:hover": !isLast
                      ? { textDecoration: "underline", color: theme.palette.primary.dark }
                      : {},
                  }}
                >
                  {item.name === "Home" && <HomeIcon fontSize="small" />}
                  {item.name}
                </Typography>
              );
            })}
          </Breadcrumbs>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={onRefresh}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Chip
            label={`${folders.length} repositor${folders.length !== 1 ? "ies" : "y"} • ${files.length} file${files.length !== 1 ? "s" : ""}`}
            size="small"
            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), fontWeight: 500 }}
          />
        </Box>
      </Box>
    </Paper>
  );
}
